import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { resolveRouterPath, router } from '../../router';
import { styles as sharedStyles } from '../../styles/shared-styles';

import '@shoelace-style/shoelace/dist/components/button/button.js';

@customElement('app-welcome')
export class AppWelcome extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100dvh;
        padding: clamp(2rem, 6vw, 4rem) clamp(1.25rem, 5vw, 2rem);
        box-sizing: border-box;
        background: var(--app-bg);
        position: relative;
        overflow: hidden;
      }

      /* Radial glow anchored behind the icon */
      :host::before {
        content: '';
        position: absolute;
        top: -5%;
        left: 50%;
        translate: -50% 0;
        width: min(560px, 130vw);
        height: min(560px, 130vw);
        background: radial-gradient(
          ellipse at center,
          rgba(15, 42, 68, 0.07) 0%,
          transparent 68%
        );
        pointer-events: none;
      }

      .content {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        max-width: 480px;
        width: 100%;
      }

      /* ── Logotype: icon + brand name side by side ── */
      .logotype {
        display: flex;
        align-items: center;
        gap: 18px;
        margin-bottom: clamp(2.25rem, 6vw, 3.25rem);
      }

      .brand-icon {
        width: clamp(56px, 14vw, 72px);
        height: clamp(56px, 14vw, 72px);
        border-radius: 18px;
        flex-shrink: 0;
        display: block;
        box-shadow: 0 4px 16px rgba(15, 42, 68, 0.22);
      }

      .brand-name {
        font-family: var(--font-display);
        font-size: clamp(1.65rem, 5vw, 2.2rem);
        font-weight: 800;
        letter-spacing: -0.03em;
        line-height: 1;
        color: #0f2a44;
      }

      .brand-name span {
        color: #f4b942;
      }

      @media (prefers-color-scheme: dark) {
        .brand-name {
          color: #e8edf2;
        }
      }

      /* ── Byline ── */
      .byline {
        font-family: var(--font-display);
        font-size: clamp(1.35rem, 3.8vw, 1.8rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.25;
        color: var(--app-text);
        margin: 0 0 0.85rem;
      }

      /* ── Supporting copy ── */
      .pitch {
        font-size: clamp(0.88rem, 2.2vw, 1rem);
        color: var(--app-text-muted);
        line-height: 1.65;
        margin: 0 0 clamp(2rem, 5vw, 2.75rem);
        max-width: 38ch;
      }

      /* ── CTAs ── */
      .cta-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        width: 100%;
        max-width: 300px;
      }

      .cta-group sl-button {
        width: 100%;
      }

      .cta-group sl-button::part(base) {
        font-size: 1rem;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .signin-link {
        font-size: 0.9rem;
        color: var(--app-text-muted);
      }

      .signin-link a {
        color: var(--brand-rose-600);
        text-decoration: none;
        font-weight: 600;
        transition: color 120ms;
      }

      .signin-link a:hover {
        color: var(--brand-rose-700);
        text-decoration: underline;
      }

      @media (prefers-color-scheme: dark) {
        .signin-link a {
          color: var(--brand-rose-400);
        }

        .signin-link a:hover {
          color: var(--brand-rose-300);
        }
      }

      /* ── Footer ── */
      .footer-links {
        position: absolute;
        bottom: clamp(1rem, 3vw, 1.75rem);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        gap: 1.5rem;
        font-size: 0.8rem;
        color: var(--app-text-muted);
      }

      .footer-links a {
        color: inherit;
        text-decoration: none;
        transition: color 120ms;
      }

      .footer-links a:hover {
        color: var(--app-text);
      }
    `,
  ];

  private _go(path: string) {
    router.navigate(resolveRouterPath(path));
  }

  render() {
    return html`
      <div class="content">
        <div class="logotype">
          <img
            class="brand-icon"
            src="/assets/icons/pag-asenso-icon.svg"
            alt=""
            draggable="false"
          />
          <div class="brand-name">Pag<span>-asenso</span></div>
        </div>

        <h1 class="byline">
          The POS that grows with you,<br />from entrepreneur to enterprise.
        </h1>

        <p class="pitch">
          Sales, stock, loyalty, and real-time kitchen flow — all in one app
          that runs on any phone or tablet. No setup fees. No surprises.
        </p>

        <div class="cta-group">
          <sl-button
            variant="primary"
            size="large"
            @click=${() => this._go('login')}
          >
            Get started — it's free
          </sl-button>
          <p class="signin-link">
            Already have an account?
            <a
              href=${resolveRouterPath('login')}
              @click=${(e: Event) => {
                e.preventDefault();
                this._go('login');
              }}
              >Sign in</a
            >
          </p>
        </div>
      </div>

      <nav class="footer-links">
        <a
          href=${resolveRouterPath('privacy')}
          @click=${(e: Event) => {
            e.preventDefault();
            this._go('privacy');
          }}
          >Privacy</a
        >
        <a
          href=${resolveRouterPath('terms')}
          @click=${(e: Event) => {
            e.preventDefault();
            this._go('terms');
          }}
          >Terms</a
        >
        <a
          href=${resolveRouterPath('about')}
          @click=${(e: Event) => {
            e.preventDefault();
            this._go('about');
          }}
          >About</a
        >
      </nav>
    `;
  }
}
